import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, FlexBox, Li, Ul, useScreenClass } from '@vp/swan';
import { ModTarget } from '@vp/mod-father';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { generateLineItemListDisplay } from '../../../util/lineItemListUtils';
import { OrderDetailsLineItem } from './DetailsLineItem';
import LineItemGroup from '../../shared/itemGrouping/LineItemGroup';
import OrderActionsProvider from '../../contexts/OrderActionsContext';

const LineItemListModWrapper = ({ children }) => {
  const screenClass = useScreenClass();
  return <Box mb={screenClass === 'xs' ? 6 : 8}>{children}</Box>;
};

LineItemListModWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const LineItemList = () => {
  const { order } = useContext(OrderDetailsContext);

  // Determine the logical sorting of line items and line item groups relative to CMRDs
  const filterOutPastArrivalDates = false;
  const lineItemListDisplay = useMemo(
    () =>
      generateLineItemListDisplay(order.lineItems, filterOutPastArrivalDates),
    [filterOutPastArrivalDates, order.lineItems]
  );

  /*
   * This line uses the LineItemGroup HOC (Higher Order Component) which allows
   * me to share the wrapping group display around both LineItem (orderdetails)
   * and LineItem (orderhistory). The HOC takes a component [and its props] as a
   * parameter and wraps the passed in component with shared grouping UI.
   * Read more: https://reactjs.org/docs/higher-order-components.html
   */
  const GroupHOC = LineItemGroup(OrderDetailsLineItem);

  return (
    <>
      <ModTarget name="aboveItems" hostModWrapper={LineItemListModWrapper} />
      <Ul skin="minimal" data-testid="items-list">
        <OrderActionsProvider orderNumber={order.orderNumber}>
          <FlexBox flexDirection="column" gap="between-subsections">
            {lineItemListDisplay.map(displayGroup =>
              displayGroup.groupId ? (
                <Li
                  key={`itemGroup-${displayGroup.groupId}`}
                  data-testid={`itemGroup-${displayGroup.groupId}`}
                >
                  <GroupHOC
                    items={displayGroup.lineItems}
                    groupId={displayGroup.groupId}
                    order={order}
                    orderNumber={order.orderNumber}
                  />
                </Li>
              ) : (
                // If a displayGroup is a standalone item (no groupId), it will contain a lineItems array with that single item: { lineItems: [ itemA ] }
                <Li key={`item-${displayGroup.lineItems[0].id}`}>
                  <OrderDetailsLineItem
                    item={displayGroup.lineItems[0]}
                    orderNumber={order.orderNumber}
                  />
                </Li>
              )
            )}
          </FlexBox>
        </OrderActionsProvider>
      </Ul>
    </>
  );
};
